import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { Bell, ChatCircle, CheckCircle } from "@phosphor-icons/react";

function relTime(d) {
    const diff = (Date.now() - new Date(d).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(d).toLocaleDateString();
}

export default function NotificationsBell() {
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const refreshCount = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications/unread-count");
            setCount(data.count || 0);
        } catch (err) {
            // non-fatal: badge stays stale until next poll
            console.error("notifications unread-count failed:", err);
        }
    }, []);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications");
            setItems(data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshCount();
        const id = setInterval(refreshCount, 30000);
        return () => clearInterval(id);
    }, [refreshCount]);

    const onOpenChange = (v) => {
        setOpen(v);
        if (v) loadAll();
    };

    const markAllRead = async () => {
        await api.post("/notifications/read-all");
        setItems((cur) => cur.map((n) => ({ ...n, is_read: true })));
        setCount(0);
    };

    const onItemClick = async (n) => {
        if (!n.is_read) {
            try {
                await api.post(`/notifications/${n.id}/read`);
            } catch (err) {
                // non-fatal: UI marks it locally, server may catch up next poll
                console.error("mark-read failed:", err);
            }
            setItems((cur) => cur.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
            setCount((c) => Math.max(0, c - 1));
        }
        if (n.link) {
            setOpen(false);
            navigate(n.link);
        }
    };

    return (
        <DropdownMenu open={open} onOpenChange={onOpenChange}>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="notifications-bell-btn"
                    className="relative w-9 h-9 rounded-full border border-stone-200 hover:border-[#990000] hover:text-[#990000] flex items-center justify-center text-stone-600 transition-colors"
                    aria-label="Notifications"
                >
                    <Bell size={18} weight={count > 0 ? "fill" : "regular"} />
                    {count > 0 && (
                        <span
                            data-testid="notifications-unread-badge"
                            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#990000] text-[#FFCC00] text-[10px] font-bold flex items-center justify-center px-1 border border-white"
                        >
                            {count > 9 ? "9+" : count}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[360px] p-0 max-h-[500px] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
                    <div className="font-heading font-semibold text-[#0A0A0A]">Notifications</div>
                    {items.some((n) => !n.is_read) && (
                        <button
                            data-testid="notifications-read-all-btn"
                            onClick={markAllRead}
                            className="text-xs text-[#990000] font-semibold hover:underline"
                        >
                            Mark all read
                        </button>
                    )}
                </div>
                <div className="overflow-y-auto flex-1">
                    {loading ? (
                        <div className="p-6 text-center text-sm text-stone-400">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-8 text-center">
                            <Bell size={28} className="mx-auto text-stone-300 mb-2" />
                            <div className="text-sm text-stone-500">You're all caught up!</div>
                        </div>
                    ) : (
                        <ul className="divide-y divide-stone-100">
                            {items.map((n) => (
                                <li key={n.id}>
                                    <button
                                        data-testid={`notification-item-${n.id}`}
                                        onClick={() => onItemClick(n)}
                                        className={`w-full text-left px-4 py-3 hover:bg-stone-50 flex gap-3 ${
                                            !n.is_read ? "bg-[#FFF4CC]/40" : ""
                                        }`}
                                    >
                                        <div className="w-8 h-8 rounded-full bg-[#990000]/10 flex items-center justify-center flex-shrink-0">
                                            {n.type === "comment"
                                                ? <ChatCircle size={16} className="text-[#990000]" weight="fill" />
                                                : <CheckCircle size={16} className="text-[#990000]" weight="fill" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-[#0A0A0A] line-clamp-1">{n.title}</div>
                                            <div className="text-xs text-stone-500 line-clamp-2 mt-0.5">{n.body}</div>
                                            <div className="text-[10px] uppercase tracking-wider text-stone-400 mt-1.5 font-bold">
                                                {relTime(n.created_at)}
                                            </div>
                                        </div>
                                        {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#990000] flex-shrink-0 mt-2" />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
